import Route from '@ember/routing/route';
import { service } from '@ember/service';

import { shouldSkipKyc } from '@repo/identity-verification-kit/utils/should-skip-kyc';
import { shouldSendToSentry } from '@repo/runtime-kit/utils/should-send-to-sentry';
import { webviewManager } from '@repo/runtime-kit/webview';
import { variation } from 'ember-launch-darkly';

import { MainLayout } from 'register-partners-js/react/components/layouts/main';
import { postRegistrationSubmitAsync } from 'register-partners-js/react/registration';
import { ErrorInfo } from 'register-partners-js/utils/error-info';
import { fetchKyc } from 'register-partners-js/utils/fetch-kyc';

export default class RegistrationKycRoute extends Route {
  mainLayout = MainLayout;

  @service sentry;
  @service deviceManager;
  @service router;
  @service segment;
  @service store;
  @service networkManager;
  @service queryClientManager;
  @service launchdarkly;

  async model() {
    let registration = this.modelFor('registration');
    let organization = registration.get('organization');
    let owner = await registration.get('owner');

    let isSingleKycEnabled = variation('feature--boolean-single-kyc');
    let kycInfo = await fetchKyc({
      sentry: this.sentry,
      queryClientManager: this.queryClientManager,
      isSingleKycEnabled,
      market: organization.get('legalCountry'),
    });

    if (shouldSkipKyc(kycInfo)) {
      this.segment.track('onboarding_partner_kyc_skipped', {
        membership_id: owner.id,
      });

      return {
        registration,
        organization,
        stakeholder: owner,
        contractSign: true,
      };
    } else if (owner.kycProviderName === 'onfido') {
      this.segment.track('onboarding_partner_kyc_started', {
        provider: owner.kycProviderName,
        is_webview: webviewManager.isWebview,
      });

      let kycSdkToken = this.store.createRecord('kyc-sdk-token', {
        stakeholder: owner,
      });

      await kycSdkToken.save();

      return {
        registration,
        organization,
        stakeholder: owner,
        kycSdkToken,
      };
    } else {
      this.segment.track('onboarding_partner_kyc_started', {
        provider: owner.kycProviderName,
        is_webview: webviewManager.isWebview,
      });
      this.segment.track('onboarding_partner_fourthline_waiting_screen_loaded');
      try {
        let kyc = this.store.createRecord('kyc', {
          stakeholder: owner,
        });
        await kyc.save();

        if (kyc.providerStatus === 'process_finished') {
          return {
            registration,
            organization,
            stakeholder: owner,
            contractSign: true,
          };
        } else {
          return {
            registration,
            organization,
            stakeholder: owner,
            kyc,
          };
        }
      } catch (error) {
        if (ErrorInfo.for(error).shouldSendToSentry) {
          this.sentry.captureException(error);
        }
        // We leverage the underlying error screen for fourthline that expects a reason.
        return {
          registration,
          stakeholder: owner,
          kyc: {
            reason: 'KYC_API_ERROR',
          },
        };
      }
    }
  }

  async afterModel({ registration, contractSign }) {
    if (contractSign) {
      try {
        await postRegistrationSubmitAsync(registration.id);
      } catch (error) {
        if (shouldSendToSentry(error)) {
          this.sentry.captureException(error);
        }
      }
    }
  }

  setupController(controller, { contractSign }) {
    super.setupController(...arguments);

    if (contractSign) {
      controller.contractSignWebSocketEnabled = true;
    }
  }
}
